/**
 * Programmatic "use" pages. Rendered by PseoPage at /<slug>.
 * Shape: see SCHEMA.md in this folder. Rules: docs/content/WRITING_RULES.md.
 * Sources: only URLs listed in docs/content research files.
 */

const UPDATED = "2026-09-28";

const SRC = {
  rele: {
    label: "Rele and Mohile 2003, Effect of mineral oil, sunflower oil and coconut oil on prevention of hair damage, J Cosmet Sci",
    url: "https://www.researchgate.net/publication/10787534_Effect_of_mineral_oil_sunflower_oil_and_coconut_oil_on_prevention_of_hair_damage",
  },
  releRecord: {
    label: "Rele and Mohile 2003, journal record with author affiliations, Society of Cosmetic Chemists library",
    url: "https://library.scconline.org/v054n02/99",
  },
  ruetsch: {
    label: "Ruetsch et al. 2001, oil penetration into hair, J Cosmet Sci (PubMed)",
    url: "https://pubmed.ncbi.nlm.nih.gov/11413497/",
  },
  agero: {
    label: "Agero and Verallo Rowell 2004, coconut oil vs mineral oil for xerosis, Dermatitis",
    url: "https://journals.sagepub.com/doi/abs/10.2310/derm.1.2004.2015",
  },
  evangelista: {
    label: "Evangelista et al. 2014, virgin coconut oil in children with atopic dermatitis, Int J Dermatol (PubMed)",
    url: "https://pubmed.ncbi.nlm.nih.gov/24320105/",
  },
  jaims: {
    label: "A review on Narikela Thaila and Narikela regarding its use and properties, Journal of Ayurveda and Integrated Medical Sciences",
    url: "https://jaims.in/jaims/article/view/1764",
  },
  fssaiStd: {
    label: "FSSAI Food Products Standards, Chapter 2.2, Fats, oils and fat emulsions (Version 5)",
    url: "https://www.fssai.gov.in/upload/uploadfiles/files/Chapter%202_2_Fats_oils%20and%20fat%20emulsions.pdf",
  },
  fssaiLabel: {
    label: "FSSAI Labelling and Display compendium",
    url: "https://fssai.gov.in/upload/uploadfiles/files/Comp_Labelling.pdf",
  },
  kerala: {
    label: "Onmanorama, 74 coconut oil brands banned in Kerala (Dec 2018)",
    url: "https://www.onmanorama.com/news/kerala/2018/12/18/adulteration-74-coconut-oil-brands-banned-in-kerala.html",
  },
  dart: {
    label: "FSSAI DART: Detect Adulteration with Rapid Test",
    url: "https://eatrightindia.gov.in/dart/",
  },
  dartSummary: {
    label: "Summary of the FSSAI DART manual, Food Safety Helpline",
    url: "https://foodsafetyhelpline.com/fssai-publishes-manual-detect-adulteration-rapid-test-dart/",
  },
  jaocs: {
    label: "Rates of oxidation of unsaturated fatty acids, JAOCS (Springer)",
    url: "https://link.springer.com/article/10.1007/BF02643258",
  },
};

const PATCH_TEST =
  "Patch test first. Put a drop on the inside of your forearm or behind the ear, leave it for 24 hours without washing, and do not use the oil if you see redness, itching, swelling or bumps.";

const CHINTU_LINE =
  "If you would like to try it before you commit to a big jar, the [Chintu Edition](/products/karijeeva-chintu-edition) is 100 ml of the same cold pressed coconut oil, small enough for a dressing table, a school bag or a travel kit.";

export const USE_PAGES = [
  /* ---------------------------------------------------------------- */
  /* 1. How to oil hair                                                */
  /* ---------------------------------------------------------------- */
  {
    slug: "how-to-oil-hair-with-coconut-oil",
    template: "use",
    keyword: "how to apply coconut oil on hair",
    title: "How to apply coconut oil on hair: a simple champi routine",
    seoTitle: "How to Apply Coconut Oil on Hair: Champi Routine",
    seoDescription:
      "How to apply coconut oil on hair: a weekly champi routine, how much to use for short or long hair, how long to leave it before washing, and how to wash it.",
    tldr:
      "Warm a teaspoon or two of coconut oil, part your hair, massage it into the scalp with your fingertips, then smooth what is left down to the ends. Leave it for at least an hour, or overnight, and wash with a mild shampoo, twice if needed. Once or twice a week is how most Indian families do it. Studies on hair are lab work on hair fibres, so treat champi as care, not treatment.",
    updated: UPDATED,
    bestFor: "Chintu Edition (100 ml)",
    bestForWhy:
      "A 100 ml jar sits easily on a dressing table and is sized for a regular hair oiling routine, without opening a big kitchen jar each time.",
    sections: [
      {
        id: "routine",
        heading: "What does a good champi routine look like?",
        paragraphs: [
          "Champi is less about the oil and more about the habit. A fixed day, a warm katori of oil and ten unhurried minutes of fingertips on the scalp. In many South Indian homes that day was Saturday or Sunday, before the head bath.",
          "Our main [coconut oil for hair guide](/coconut-oil-for-hair) explains why families oil their hair and what the research shows. This page is the practical version: the routine, the amounts and the timing, in one place you can keep open on your phone.",
        ],
        steps: [
          "Warm the oil. Stand the jar in a bowl of warm water for a few minutes. It should feel pleasantly warm on your wrist, never hot. In winter this also melts oil that has set white.",
          "Comb out tangles first, then part your hair into four or five sections.",
          "Dip your fingertips in the oil and press it onto the scalp along each parting.",
          "Massage in small circles with the pads of your fingers, not your nails, for five to ten minutes.",
          "Rub what is left on your palms down the lengths and into the ends.",
          "Loosely plait or tie your hair and leave it. Use an old towel on the pillow if you leave it overnight.",
          "Wash with a mild shampoo and lukewarm water. Shampoo a second time if your hair still feels greasy.",
        ],
      },
      {
        id: "how-much",
        heading: "How much coconut oil should you use for short or long hair?",
        paragraphs: [
          "Start small. Too much oil is the main reason people give up on champi, because it takes two or three washes to get out. A teaspoon is about 5 ml, so these amounts are smaller than most people expect.",
        ],
        table: {
          caption: "Starting amounts of coconut oil by hair length",
          head: ["Hair length", "Start with", "Where to focus"],
          rows: [
            ["Short (above the ears)", "1 to 2 teaspoons", "Mostly the scalp"],
            ["Chin to shoulder", "2 to 3 teaspoons", "Scalp first, then the ends"],
            ["Below the shoulders", "About 1 tablespoon", "Scalp, then extra on dry ends"],
            ["Waist length or very thick", "1 to 2 tablespoons", "Work section by section so no part of the scalp is missed"],
            ["Children", "Half to 1 teaspoon", "Light touch, keep it away from the eyes"],
          ],
        },
      },
      {
        id: "how-long",
        heading: "How long should you leave coconut oil on your hair?",
        paragraphs: [
          "At least an hour before you wash. Many people do a pre wash oiling in the morning and wash before lunch, while others prefer to oil at night and wash the next morning. Both are traditional, so pick the one that fits your week.",
          "The study most often quoted on this is by Rele and Mohile (2003). In lab tests on hair fibres, coconut oil applied before washing reduced protein loss, and it was the only one of the three oils tested to do so. It was lab work on hair samples, not a trial on people, and the authors worked in the research unit of a company that sells coconut hair oil.",
        ],
        table: {
          caption: "Pre wash oiling or overnight oiling",
          head: ["", "Pre wash (1 to 2 hours)", "Overnight"],
          rows: [
            ["Best for", "Busy weekdays, oily scalps", "A slow weekend ritual, dry lengths"],
            ["Pillow", "No towel needed", "Use an old towel or pillow cover"],
            ["Washing", "Usually one or two shampoos", "Often two shampoos"],
            ["In winter", "Warm the oil so it spreads easily", "Warm the oil and keep the room warm"],
          ],
        },
      },
      {
        id: "mistakes",
        heading: "What are the common mistakes with hair oiling?",
        list: [
          "Using too much, then scrubbing hard to get it out.",
          "Heating the oil on the stove until it is hot. A warm water bath is enough.",
          "Scratching the scalp with nails during the massage.",
          "Dipping wet or oily fingers into the main jar. Pour a little into a katori instead.",
          "Expecting oil to fix hair fall or dandruff. For those, see a dermatologist.",
        ],
        paragraphs: [
          "If your scalp feels dry and tight, read our gentler routine for [dry scalp](/coconut-oil-for-dry-scalp).",
        ],
      },
      {
        id: "objection",
        heading: "Is cold pressed coconut oil worth it just for hair?",
        paragraphs: [
          "It costs more than a refined or perfumed hair oil, and we will not pretend otherwise. What you get is one ingredient, coconut, pressed from copra grown on our family farms in Tiptur and on the Karnataka coast, with nothing added. It keeps its natural coconut smell, which some people love and a few find strong.",
          "Every batch goes to an independent food testing lab, and you can read the latest report on our [lab reports page](/lab-reports). " +
            CHINTU_LINE +
            " If you already cook with coconut oil, the same oil from our [jars](/products) can serve both.",
        ],
      },
    ],
    faqs: [
      {
        q: "How to apply coconut oil on hair properly?",
        a: "Warm it gently, part your hair into sections, massage it into the scalp with your fingertips for five to ten minutes, then smooth the rest down to the ends. Leave it for at least an hour and wash with a mild shampoo.",
      },
      {
        q: "How long should I keep coconut oil in my hair?",
        a: "At least one hour before washing. Many people leave it overnight with an old towel on the pillow. Longer does not have to mean better, so choose what fits your routine.",
      },
      {
        q: "Should coconut oil be applied on wet or dry hair?",
        a: "Most families apply it on dry hair before a head bath, which is the traditional pre wash champi. A few drops on damp ends after washing also works if your ends feel dry.",
      },
      {
        q: "How many times a week should I oil my hair?",
        a: "Once or twice a week is the common habit. If your scalp feels oily quickly, once a week is plenty.",
      },
      {
        q: "Can I leave coconut oil in my hair every day?",
        a: "You can, but most people find daily oil leaves hair limp and needs more washing. If you want a little every day, use one or two drops on the ends only.",
      },
    ],
    sources: [SRC.rele, SRC.releRecord, SRC.ruetsch],
    related: [
      { label: "Coconut oil for hair", to: "/coconut-oil-for-hair" },
      { label: "Coconut oil for dry scalp", to: "/coconut-oil-for-dry-scalp" },
      { label: "Why coconut oil turns white", to: "/why-does-coconut-oil-turn-white" },
      { label: "Karijeeva Chintu Edition", to: "/products/karijeeva-chintu-edition" },
    ],
  },

  /* ---------------------------------------------------------------- */
  /* 2. Dry scalp                                                      */
  /* ---------------------------------------------------------------- */
  {
    slug: "coconut-oil-for-dry-scalp",
    template: "use",
    keyword: "coconut oil for dry scalp",
    title: "Coconut oil for dry scalp: a gentle routine, and when to see a doctor",
    seoTitle: "Coconut Oil for Dry Scalp: How to Use It Gently",
    seoDescription:
      "Using coconut oil for dry scalp the traditional way: a weekly routine, how much to use, what research does not show, and when to see a dermatologist.",
    tldr:
      "Many Indian families oil a dry, tight feeling scalp with coconut oil before a head bath, and many people find the scalp feels more comfortable afterwards. Use a small amount, massage gently, leave it an hour or two and wash it out well. There is no good evidence that coconut oil treats dandruff or any scalp condition. If flaking lasts, itches or comes with redness, see a dermatologist.",
    updated: UPDATED,
    bestFor: "Chintu Edition (100 ml)",
    bestForWhy:
      "A small jar is an easy way to see how your scalp responds over a few weeks before buying a larger one.",
    sections: [
      {
        id: "tradition",
        heading: "Why do people use coconut oil for a dry scalp?",
        paragraphs: [
          "In South India, oiling the head before a bath has been part of family life for generations. When the scalp feels tight and dry, especially in winter or after hard water washes, the first thing many grandmothers reach for is the coconut oil jar.",
          "Classical Ayurvedic texts, as summarised in modern reviews, describe coconut oil as keshya, a word usually read as good for the hair. That is a traditional description, not a clinical finding, and we share it as heritage only.",
        ],
      },
      {
        id: "evidence",
        heading: "What does research say about coconut oil and the scalp?",
        paragraphs: [
          "Very little, directly. The well known studies on coconut oil and hair were done in the lab on hair fibres. Rele and Mohile (2003) found coconut oil reduced protein loss from hair, and Ruetsch and colleagues (2001) found it passes into the hair shaft. Neither study looked at the scalp or at flaking.",
          "So we can describe coconut oil as a simple, traditional scalp oil that many people find comfortable. We cannot tell you it treats dryness, dandruff or any scalp condition, and you should be wary of anyone who does.",
        ],
      },
      {
        id: "routine",
        heading: "How to oil a dry scalp, step by step",
        steps: [
          PATCH_TEST,
          "Warm a teaspoon or two of oil in a katori standing in warm water.",
          "Part your hair and dab the oil onto the scalp along each parting with your fingertips.",
          "Massage gently with the pads of your fingers for about five minutes. Do not scratch or pick at flakes.",
          "Leave it for one to two hours. On a scalp that flakes, shorter is usually kinder than overnight.",
          "Wash with a mild shampoo and lukewarm water, not hot. Rinse well so no oil is left at the roots.",
          "Repeat once a week and watch how your scalp responds over three or four weeks.",
        ],
        paragraphs: [
          "For the full hair routine with amounts by hair length, see [how to oil your hair with coconut oil](/how-to-oil-hair-with-coconut-oil).",
        ],
      },
      {
        id: "doctor",
        heading: "When should you see a dermatologist instead?",
        paragraphs: [
          "It is not easy to tell a dry scalp from other scalp conditions at home. Some people find that oil left on for a long time makes flaking worse, not better. That is a sign to stop and get it checked.",
        ],
        list: [
          "Flaking that lasts more than a few weeks, or keeps coming back.",
          "Itching that wakes you up or makes you scratch until it hurts.",
          "Red, sore, oozing or crusted patches.",
          "Flakes that spread to the eyebrows, ears or sides of the nose.",
          "Hair loss in patches.",
        ],
      },
      {
        id: "objection",
        heading: "Will plain coconut oil feel too greasy on my scalp?",
        paragraphs: [
          "It can if you use too much. Start with a teaspoon, not a handful, and wash it out properly. If your scalp tends to be oily, a short pre wash oiling of an hour is often enough.",
          "For a scalp that feels dry, fewer ingredients is a sensible choice. Karijeeva is one ingredient, cold pressed coconut oil with no perfume, colour or blended oils, and every batch is tested at an independent food testing lab ([see the reports](/lab-reports)). " +
            CHINTU_LINE,
        ],
      },
    ],
    faqs: [
      {
        q: "Is coconut oil good for dry scalp?",
        a: "Many people find their scalp feels more comfortable after a traditional coconut oil champi. There are no good clinical trials on coconut oil and dry scalp, so treat it as gentle care, not treatment. See a dermatologist if flaking lasts.",
      },
      {
        q: "Does coconut oil cure dandruff?",
        a: "We do not claim that, and the evidence is not there. Some people find oil makes flaking worse. A persistent itchy or flaky scalp needs a dermatologist.",
      },
      {
        q: "How long should I leave coconut oil on a dry scalp?",
        a: "One to two hours before washing is a good start. If your scalp flakes, a shorter time is usually better than overnight. Rinse well so no oil stays at the roots.",
      },
      {
        q: "Can I apply coconut oil on scalp daily for dryness?",
        a: "Once a week is a better start. Daily oiling means daily washing, and hot water and frequent shampoo can leave the scalp feeling drier. Watch how your scalp responds over a few weeks.",
      },
      {
        q: "Should I warm coconut oil before putting it on my scalp?",
        a: "Warm, not hot. Stand the jar or a katori in warm water for a few minutes and test a drop on your wrist first. Never apply hot oil to the scalp.",
      },
    ],
    sources: [SRC.rele, SRC.ruetsch, SRC.jaims],
    related: [
      { label: "How to oil your hair with coconut oil", to: "/how-to-oil-hair-with-coconut-oil" },
      { label: "Coconut oil for hair", to: "/coconut-oil-for-hair" },
      { label: "Coconut oil for skin", to: "/coconut-oil-for-skin" },
      { label: "Our lab reports", to: "/lab-reports" },
    ],
  },

  /* ---------------------------------------------------------------- */
  /* 3. Lips                                                           */
  /* ---------------------------------------------------------------- */
  {
    slug: "coconut-oil-for-lips",
    template: "use",
    keyword: "coconut oil for lips",
    title: "Coconut oil for lips: a simple habit for dry weather",
    seoTitle: "Coconut Oil for Lips: How to Use It for Dry Lips",
    seoDescription:
      "How to use coconut oil for lips: when to apply it, how to keep a small pot clean, what one dry skin study found, and when chapped lips need a doctor.",
    tldr:
      "Rub a tiny amount of coconut oil onto clean lips with a fingertip before bed and whenever they feel dry. It works as a simple moisturiser, and in a small 2004 trial coconut oil did as well as mineral oil on dry skin (not lips specifically). Because a food grade oil is edible, it is a sensible choice for lips. It is not a sunscreen, and cracks that bleed or will not heal need a doctor.",
    updated: UPDATED,
    bestFor: "Chintu Edition (100 ml)",
    bestForWhy:
      "A 100 ml jar is easy to keep on a dressing table or in a bag, and you can fill a tiny lip pot from it.",
    sections: [
      {
        id: "why",
        heading: "Why use coconut oil on lips?",
        paragraphs: [
          "Lips have almost no oil glands of their own, so they dry out quickly in cold wind, air conditioning and dry weather. A thin layer of oil slows that moisture loss, which is why so many homes simply use a dab of the coconut oil from the kitchen.",
          "There is a practical reason too. Whatever you put on your lips, you end up swallowing a little. A single ingredient, food grade coconut oil is something you would cook with anyway, which many parents like for children's lips.",
        ],
      },
      {
        id: "evidence",
        heading: "What does research say?",
        paragraphs: [
          "There is no trial we know of on coconut oil and lips. The closest is a small 2004 double blind trial by Agero and Verallo Rowell in 34 people with dry skin, where extra virgin coconut oil was as effective and safe as mineral oil over two weeks. It was a small, short trial on body skin, it used virgin oil (Karijeeva is cold pressed coconut oil, not virgin), and it showed coconut oil on par with a basic moisturiser, not better.",
        ],
      },
      {
        id: "how-to",
        heading: "How to use coconut oil on lips",
        table: {
          caption: "When and how to use coconut oil on your lips",
          head: ["When", "How much", "Tip"],
          rows: [
            ["Before bed", "A rice grain sized amount", "The best time, as it stays on longest"],
            ["Morning, before lipstick", "Less than a rice grain", "Blot after a minute so lipstick does not slide"],
            ["Dry weather or travel", "A tiny dab as needed", "Carry a small clean pot or tin"],
            ["Winter, when the oil is solid", "Scrape a little with a clean nail or spatula", "It melts at once on warm lips"],
          ],
        },
        list: [
          PATCH_TEST,
          "Take oil out with a clean, dry fingertip or spatula so the pot stays clean.",
          "Do not lick your lips after applying, as that dries them further.",
        ],
      },
      {
        id: "limits",
        heading: "When is coconut oil not enough for lips?",
        paragraphs: [
          "Oil is a moisturiser, not a treatment. See a doctor or pharmacist in any of these cases.",
        ],
        list: [
          "Outdoors in strong sun. Coconut oil is not a sunscreen, so use a lip balm with SPF.",
          "Cracks at the corners of the mouth that sting or do not heal in a week or two.",
          "Lips that bleed, swell, blister or peel badly.",
          "Cold sores. Keep your fingers and oil pot away from them and ask a doctor or pharmacist.",
        ],
      },
      {
        id: "objection",
        heading: "Does it smell of coconut on the lips?",
        paragraphs: [
          "Yes, lightly. Karijeeva is not deodorised, so it keeps its natural coconut smell and taste. Most people find that pleasant on the lips, but if you prefer no smell at all, a refined oil or an unscented balm may suit you better.",
          "If you like it, one small jar does a lot, because lips need so little. " +
            CHINTU_LINE +
            " The same oil also works for [dry skin](/coconut-oil-for-skin) and [cracked heels](/coconut-oil-for-cracked-heels).",
        ],
      },
    ],
    faqs: [
      {
        q: "Is coconut oil good for dry lips?",
        a: "It works as a simple moisturiser, and many people use it before bed and in dry weather. There is no trial on lips specifically. A small trial on dry body skin found coconut oil did as well as mineral oil.",
      },
      {
        q: "Can I apply coconut oil on lips overnight?",
        a: "Yes, that is when most people find it works best. Use a very small amount on clean lips so it does not spread onto the pillow.",
      },
      {
        q: "Is coconut oil safe for children's lips?",
        a: "Many families use it, and a food grade, single ingredient oil is a sensible choice because children lick their lips. Patch test first, use a tiny amount and see a doctor for cracks that bleed or do not heal.",
      },
      {
        q: "Can coconut oil make lips pink?",
        a: "We do not claim that. Lip colour depends on many things, including sun and genes. Coconut oil can make dry lips look smoother and less flaky, which is all we would say.",
      },
      {
        q: "Can I use coconut oil instead of lip balm?",
        a: "For dryness indoors, many people do. Outdoors in the sun, use a lip balm with SPF, because coconut oil is not a sunscreen.",
      },
    ],
    sources: [SRC.agero],
    related: [
      { label: "Coconut oil for skin", to: "/coconut-oil-for-skin" },
      { label: "Coconut oil for cracked heels", to: "/coconut-oil-for-cracked-heels" },
      { label: "Coconut oil as makeup remover", to: "/coconut-oil-as-makeup-remover" },
      { label: "Karijeeva Chintu Edition", to: "/products/karijeeva-chintu-edition" },
    ],
  },

  /* ---------------------------------------------------------------- */
  /* 4. Cracked heels                                                  */
  /* ---------------------------------------------------------------- */
  {
    slug: "coconut-oil-for-cracked-heels",
    template: "use",
    keyword: "coconut oil for cracked heels",
    title: "Coconut oil for cracked heels: a night routine with socks",
    seoTitle: "Coconut Oil for Cracked Heels: A Night Routine",
    seoDescription:
      "Coconut oil for cracked heels: a soak, oil and socks night routine, how often to do it, what one dry skin trial found, and when to see a doctor.",
    tldr:
      "Soak your feet in warm water, pat them dry, rub a thin layer of coconut oil into the heels and put on cotton socks before bed. Repeat most nights. Coconut oil works as a simple moisturiser, and in a small 2004 trial it did as well as mineral oil on dry skin. Deep cracks that bleed or hurt, and any foot cracks if you have diabetes, need a doctor.",
    updated: UPDATED,
    bestFor: "Chintu Edition (100 ml)",
    bestForWhy:
      "A 100 ml jar can live by the bed for a nightly foot routine, away from the kitchen jar.",
    sections: [
      {
        id: "why",
        heading: "Why do heels crack, and why oil them?",
        paragraphs: [
          "The skin on the heels is thick and has few oil glands. Walking barefoot on stone floors, open footwear, hard water and dry winters all make it drier, and dry thick skin splits when it takes your weight.",
          "Oiling the heels at night is an old habit in Indian homes. Oil sits on the skin and slows the loss of water, so it works best on skin that has just been soaked. This is moisturising, not medicine.",
        ],
      },
      {
        id: "night-routine",
        heading: "The night routine, step by step",
        steps: [
          PATCH_TEST,
          "Soak your feet in a basin of warm water for ten minutes.",
          "Gently rub the heels with a pumice stone or foot scrubber. Stop well before it feels sore, and never cut or peel hard skin yourself.",
          "Pat the feet dry, leaving the skin slightly damp.",
          "Warm half a teaspoon of coconut oil between your palms and massage it into each heel and the sides of the foot.",
          "Put on clean cotton socks so the oil stays on your feet and off the bedsheet.",
          "In the morning, wipe off any extra oil before you walk on smooth floors.",
        ],
        table: {
          caption: "How often to oil cracked heels",
          head: ["How your heels look", "How often", "Soak first"],
          rows: [
            ["Dry and rough, no cracks", "Two or three nights a week", "Optional"],
            ["Shallow cracks, not painful", "Every night", "Yes"],
            ["Deep, painful or bleeding cracks", "See a doctor before home care", "Ask your doctor"],
          ],
        },
      },
      {
        id: "evidence",
        heading: "What does research say about coconut oil for dry skin?",
        paragraphs: [
          "The best known trial is by Agero and Verallo Rowell (2004): 34 people with dry skin, two weeks, extra virgin coconut oil against mineral oil. Coconut oil was as effective and as safe as mineral oil for skin hydration. It was small and short, it did not look at heels, and it used virgin oil, which Karijeeva is not.",
          "So we describe coconut oil as a simple moisturiser for dry heels. We describe it only as a moisturiser, and no study we cite looked at cracked heels.",
        ],
      },
      {
        id: "doctor",
        heading: "When should you see a doctor for cracked heels?",
        list: [
          "If you have diabetes, poor circulation or numb feet. Any crack or sore on the foot should be checked by a doctor, and do not use pumice stones or blades without their advice.",
          "Cracks that are deep, bleed, or hurt when you walk.",
          "Redness, warmth, swelling or pus around a crack.",
          "Thick, itchy or peeling skin between the toes or on the soles.",
          "Heels that do not improve after two or three weeks of regular care.",
        ],
      },
      {
        id: "objection",
        heading: "Will oily feet make the floor slippery?",
        paragraphs: [
          "Yes, if you walk barefoot straight after. That is why the socks matter, and why we suggest a thin layer, not a thick coat. Wipe your feet in the morning, especially on tiles and marble.",
          "For feet, a plain, single ingredient oil is a sensible choice, with no perfume to sting a crack. " +
            CHINTU_LINE +
            " For the rest of the body, see our [coconut oil for skin guide](/coconut-oil-for-skin) and [body massage routine](/coconut-oil-for-body-massage).",
        ],
      },
    ],
    faqs: [
      {
        q: "Is coconut oil good for cracked heels?",
        a: "Many people use it as a simple moisturiser for dry heels, especially at night with socks. A small trial found coconut oil as effective as mineral oil for dry skin. Deep or painful cracks need a doctor.",
      },
      {
        q: "How to use coconut oil for cracked heels at night?",
        a: "Soak your feet in warm water, pat them dry, rub a thin layer of coconut oil into the heels and put on cotton socks. Wipe off any extra oil in the morning.",
      },
      {
        q: "Can diabetics use coconut oil on cracked heels?",
        a: "Please ask your doctor first. With diabetes, any crack or sore on the foot should be checked, and scrubbing or cutting hard skin at home can be risky.",
      },
      {
        q: "How long does it take for heels to feel smoother with coconut oil?",
        a: "We cannot promise a timeline. Many people notice softer skin within a week or two of nightly oiling, but it depends on how dry the skin is. If nothing changes in two or three weeks, see a doctor.",
      },
      {
        q: "Can I mix coconut oil with anything for cracked heels?",
        a: "Plain coconut oil is enough, and adding things raises the chance of irritation. Avoid mixing in lemon juice or strong essential oils on cracked skin.",
      },
    ],
    sources: [SRC.agero],
    related: [
      { label: "Coconut oil for skin", to: "/coconut-oil-for-skin" },
      { label: "Coconut oil for body massage", to: "/coconut-oil-for-body-massage" },
      { label: "Coconut oil for lips", to: "/coconut-oil-for-lips" },
      { label: "Karijeeva Chintu Edition", to: "/products/karijeeva-chintu-edition" },
    ],
  },

  /* ---------------------------------------------------------------- */
  /* 5. Makeup remover                                                 */
  /* ---------------------------------------------------------------- */
  {
    slug: "coconut-oil-as-makeup-remover",
    template: "use",
    keyword: "coconut oil as makeup remover",
    title: "Coconut oil as makeup remover: how to use it, and who should skip it",
    seoTitle: "Coconut Oil as Makeup Remover: How to Use It",
    seoDescription:
      "How to use coconut oil as makeup remover: a step by step oil cleanse, eye makeup tips, a patch test, and why acne prone skin may find it too heavy.",
    tldr:
      "Massage a few drops of coconut oil over dry, made up skin, wipe it off with a warm damp cloth, then wash your face with a gentle face wash. Oil loosens makeup, including many long wear products. Patch test first, keep it out of your eyes, and skip it if your skin is acne prone, because coconut oil can feel heavy and some people break out.",
    updated: UPDATED,
    bestFor: "Chintu Edition (100 ml)",
    bestForWhy:
      "A 100 ml jar fits a dressing table or a travel kit, and you only need a few drops each night.",
    sections: [
      {
        id: "why",
        heading: "Why does oil remove makeup?",
        paragraphs: [
          "Many foundations, kajal, long wear lipsticks and waterproof mascaras are made to resist water. Oil loosens them, which is why oil cleansing has become popular and why many Indian women have used a dab of coconut oil to take off kajal for years.",
          "It is a practical trick, not a skin treatment. The oil does the loosening, and a gentle face wash afterwards takes the oil away.",
        ],
      },
      {
        id: "steps",
        heading: "How to remove makeup with coconut oil, step by step",
        steps: [
          PATCH_TEST,
          "Take out contact lenses first, and tie back your hair.",
          "Warm four or five drops of oil between clean, dry palms. If the oil is solid in winter, it melts in a few seconds.",
          "Massage it over your dry face in small circles for about a minute, including the lips and the edge of the hairline.",
          "For eye makeup, press a cotton pad with a drop of oil on closed eyelids for a few seconds, then wipe gently outward. Do not rub.",
          "Wipe everything off with a soft cloth wrung out in warm water.",
          "Wash your face with a gentle face wash to remove the leftover oil, and pat dry.",
        ],
      },
      {
        id: "skin-types",
        heading: "Which skin types suit coconut oil as a remover?",
        table: {
          caption: "Coconut oil as makeup remover by skin type",
          head: ["Skin type", "Our suggestion"],
          rows: [
            ["Dry or normal", "Usually comfortable. Follow with a gentle face wash."],
            ["Combination", "Try it on the cheeks first, and watch the T zone for a week or two."],
            ["Oily or acne prone", "Better to skip it. Coconut oil is heavy and some people break out."],
            ["Sensitive or with a skin condition", "Ask your dermatologist first."],
          ],
        },
        paragraphs: [
          "Our [coconut oil for skin guide](/coconut-oil-for-skin) explains more about using it on the face and how to go carefully.",
        ],
      },
      {
        id: "eyes",
        heading: "Is coconut oil safe around the eyes?",
        paragraphs: [
          "Use very little, keep your eyes closed, and wipe outward. A little oil in the eye can blur your vision for a while. If your eyes sting, water or turn red, rinse with clean water and stop using oil around the eyes.",
          "If you have an eye infection, a stye, or have just had eye surgery, do not use oil near the eyes and follow your doctor's advice.",
        ],
      },
      {
        id: "objection",
        heading: "Will it leave my face greasy?",
        paragraphs: [
          "Only if you skip the second wash. The oil step loosens makeup, and the gentle face wash step clears the oil. Used this way, most people with dry or normal skin find their face feels clean and soft.",
          "Makeup remover wipes and jars often list many ingredients. Karijeeva lists one: coconut. It is cold pressed, not refined, and every batch is tested at an independent food testing lab ([see our lab reports](/lab-reports)). " +
            CHINTU_LINE,
        ],
      },
    ],
    faqs: [
      {
        q: "Can I use coconut oil as makeup remover daily?",
        a: "Many people with dry or normal skin do. Always follow with a gentle face wash so no oil stays on the skin. If you notice breakouts, stop.",
      },
      {
        q: "Does coconut oil remove waterproof mascara?",
        a: "Oil loosens most waterproof mascara. Hold an oiled cotton pad on closed lids for a few seconds, then wipe gently outward without rubbing.",
      },
      {
        q: "Is coconut oil good for acne prone skin as a makeup remover?",
        a: "We suggest skipping it. Coconut oil is heavy, and many people with acne prone skin find it causes breakouts. Ask your dermatologist for a remover that suits you.",
      },
      {
        q: "Should I wash my face after removing makeup with coconut oil?",
        a: "Yes. The oil loosens makeup, and a gentle face wash removes the oil. Leaving it on overnight can feel heavy, and some people get breakouts.",
      },
      {
        q: "Can coconut oil go in my eyes?",
        a: "Try not to let it. A small amount can blur vision for a while. If your eyes sting or turn red, rinse with clean water and stop using oil near the eyes.",
      },
    ],
    sources: [SRC.agero],
    related: [
      { label: "Coconut oil for skin", to: "/coconut-oil-for-skin" },
      { label: "Coconut oil for lips", to: "/coconut-oil-for-lips" },
      { label: "Coconut oil for body massage", to: "/coconut-oil-for-body-massage" },
      { label: "Karijeeva Chintu Edition", to: "/products/karijeeva-chintu-edition" },
    ],
  },

  /* ---------------------------------------------------------------- */
  /* 6. Body massage                                                   */
  /* ---------------------------------------------------------------- */
  {
    slug: "coconut-oil-for-body-massage",
    template: "use",
    keyword: "coconut oil for body massage",
    title: "Coconut oil for body massage: a simple self massage before your bath",
    seoTitle: "Coconut Oil for Body Massage: A Self Massage Guide",
    seoDescription:
      "Coconut oil for body massage: the abhyanga style self massage tradition, how to warm the oil, how much to use, the order of strokes and bathroom safety.",
    tldr:
      "Warm a few tablespoons of coconut oil in a water bath, massage it into your body from the head down with long strokes on the limbs and circles on the joints, rest for 15 to 20 minutes, then bathe in warm water. This is the South Indian tradition of oil massage before a bath, known in Ayurveda as abhyanga. It is a ritual for comfort and care, not a treatment. Take care on the bathroom floor, which gets slippery.",
    updated: UPDATED,
    bestFor: "Chintu Edition (100 ml)",
    bestForWhy:
      "A 100 ml jar is a low commitment way to try a weekly oil bath and see if coconut oil suits your skin.",
    sections: [
      {
        id: "tradition",
        heading: "What is abhyanga, and why coconut oil?",
        paragraphs: [
          "Abhyanga is the Ayurvedic word for oil massage of the body. In many South Indian homes it survives as the weekly oil bath, the Saturday or festival morning when everyone is oiled from head to toe before a warm bath.",
          "Sesame oil is the oil the classical texts usually name for daily oil massage. Coconut oil has long been used for oil massage and head oiling in South India, and it appears among the oils discussed in classical Ayurvedic texts. We share that as tradition. It is not a health claim.",
        ],
      },
      {
        id: "warm",
        heading: "How to warm coconut oil for massage",
        paragraphs: [
          "Use a water bath, never a direct flame. Pour the oil you need into a steel katori and stand it in a larger bowl of hot water for a few minutes. In winter this also melts oil that has turned solid.",
          "Test a few drops on the inside of your wrist. It should feel comfortably warm, never hot. Oil heated on the stove can burn skin before it feels hot to the fingertips.",
        ],
      },
      {
        id: "how-to",
        heading: "How to do a coconut oil self massage",
        steps: [
          PATCH_TEST,
          "Sit on an old towel in a warm room without a draught.",
          "Start with the scalp, then the face lightly if your skin suits it, then the ears and neck.",
          "Use long strokes along the arms and legs, and small circles over the shoulders, elbows, knees and ankles.",
          "Use slow clockwise circles over the stomach and gentle strokes over the chest.",
          "Finish with the soles of the feet, then rest for 15 to 20 minutes.",
          "Bathe in warm water. Many families wash the oil off with gram flour paste or a mild soap.",
        ],
        table: {
          caption: "How much coconut oil to warm for a body massage",
          head: ["Area", "Start with"],
          rows: [
            ["Scalp", "1 to 2 teaspoons"],
            ["Arms and hands", "1 to 2 teaspoons"],
            ["Legs and feet", "2 to 3 teaspoons"],
            ["Back, chest and stomach", "About 2 teaspoons"],
            ["Whole body for one adult", "About 3 to 4 tablespoons"],
          ],
        },
      },
      {
        id: "safety",
        heading: "What safety points matter most?",
        list: [
          "The bathroom floor gets slippery. Keep a rubber mat down, sit on a stool, and wipe your soles before you stand.",
          "Coconut oil sets solid below about 24 degrees Celsius and can build up in drains. Wipe the floor and bucket with an old cloth, and do not pour oil down the drain.",
          "Skip massage on broken skin, rashes, fresh injuries or areas that are swollen and painful.",
          "If you are pregnant, have a heart condition, or have just had surgery, ask your doctor before a full body massage.",
          "For babies, follow our separate [baby massage guide](/coconut-oil-for-baby-massage), which covers patch testing and the paediatrician's advice.",
        ],
      },
      {
        id: "objection",
        heading: "Is cold pressed oil too costly to use on the whole body?",
        paragraphs: [
          "A body massage uses more oil than hair or lips, so it is a fair question. Some families keep a big jar for the weekly oil bath and use a smaller one for daily needs. Once you know you like it, a larger kitchen jar from our [range](/products) can serve the oil bath too.",
          "What you pay for is one ingredient, cold pressed from coconuts grown on our family farms, with no mineral oil or cheaper oils blended in, and a lab report for every batch on our [lab reports page](/lab-reports). " +
            CHINTU_LINE,
        ],
      },
    ],
    faqs: [
      {
        q: "Is coconut oil good for body massage?",
        a: "It has long been used for oil massage in South Indian homes, and many people find it comfortable on dry skin. A small trial found coconut oil worked as well as mineral oil as a moisturiser for dry skin. Treat massage as care and comfort, not treatment.",
      },
      {
        q: "Should coconut oil be warmed before massage?",
        a: "Yes, gently. Stand a katori of oil in hot water for a few minutes and test it on your wrist. It should feel warm, never hot.",
      },
      {
        q: "How long should I keep coconut oil on my body before bath?",
        a: "Most people rest for 15 to 20 minutes after the massage, then bathe in warm water. There is no fixed rule, so choose what fits your morning.",
      },
      {
        q: "Is coconut oil good for body massage in winter?",
        a: "Many families use it all year. In winter it turns solid, so melt it in a water bath before use and keep the room warm so the oil stays liquid on your skin.",
      },
      {
        q: "Which is better for body massage, coconut oil or sesame oil?",
        a: "Both are traditional. Sesame oil is named more often in classical Ayurvedic texts, and coconut oil has long been used in South India. Choose the one your skin likes and your family is used to.",
      },
    ],
    sources: [SRC.jaims, SRC.agero, SRC.jaocs],
    related: [
      { label: "Coconut oil for skin", to: "/coconut-oil-for-skin" },
      { label: "Coconut oil for baby massage", to: "/coconut-oil-for-baby-massage" },
      { label: "Coconut oil for cracked heels", to: "/coconut-oil-for-cracked-heels" },
      { label: "How to oil your hair with coconut oil", to: "/how-to-oil-hair-with-coconut-oil" },
    ],
  },

  /* ---------------------------------------------------------------- */
  /* 7. Diya                                                           */
  /* ---------------------------------------------------------------- */
  {
    slug: "coconut-oil-for-diya",
    template: "use",
    keyword: "coconut oil for diya",
    title: "Coconut oil for diya and pooja lamps: wicks, care and safety",
    seoTitle: "Coconut Oil for Diya: How to Light a Pooja Lamp",
    seoDescription:
      "Using coconut oil for diya and pooja lamps: the tradition, how to prepare a cotton wick, how much oil to pour, winter tips and safety with children.",
    tldr:
      "Coconut oil has long been used to light diyas and brass lamps in South Indian homes and temples. Fill the lamp with clean coconut oil, soak a cotton wick, keep only a short tip above the oil and trim it when it chars. In winter, melt the oil before filling. Never leave a lit lamp unattended, and keep it away from curtains, children and pets.",
    updated: UPDATED,
    bestFor: "500 ml",
    bestForWhy:
      "A 500 ml jar covers daily lamp lighting and the kitchen, and it is the same edible oil in both places.",
    sections: [
      {
        id: "tradition",
        heading: "Why is coconut oil used for diyas?",
        paragraphs: [
          "Along the South Indian coast and in much of Karnataka and Kerala, the lamp in the pooja room has often been lit with coconut oil, simply because it is the oil every home has. Other regions use sesame oil, mustard oil or ghee. Each family follows its own custom, and none of them is wrong.",
          "Many people feel that the oil offered to God should be the best oil in the house. That is one reason families use the same pure, edible coconut oil they cook with, rather than a cheap lamp oil of unknown origin.",
        ],
      },
      {
        id: "how-to",
        heading: "How to light a diya with coconut oil, step by step",
        steps: [
          "Clean the lamp. Wipe out old oil and burnt wick bits with a cloth, and wash brass lamps now and then with tamarind or a mild cleaner, then dry fully.",
          "Roll or twist a clean cotton wick (hoovathi or batti) so it is firm, not fluffy.",
          "Place the wick in the lamp and pour coconut oil over it until the wick is fully soaked.",
          "Fill the lamp to below the rim, never to the brim, so oil does not spill when the flame warms it.",
          "Pull the wick so only a short tip rests above the oil at the lip of the lamp.",
          "Light the tip. If the flame is tall or smoky, pull the wick back a little with a clean stick.",
        ],
      },
      {
        id: "wick-care",
        heading: "How do you keep the flame steady and clean?",
        table: {
          caption: "Common diya problems and simple fixes",
          head: ["What you see", "Likely reason", "What to do"],
          rows: [
            ["Tall, smoky flame and black soot", "Too much wick above the oil", "Pull the wick back so a shorter tip shows"],
            ["Flame keeps going out", "Wick too short, or oil has set solid", "Pull a little more wick out, or melt the oil first"],
            ["Crackling or sputtering", "Water in the lamp or a damp wick", "Dry the lamp fully and use a dry wick"],
            ["Charred black tip", "Normal after some time", "Pinch or trim off the charred part before relighting"],
          ],
        },
        paragraphs: [
          "Coconut oil sets solid below about 24 degrees Celsius, so on cold mornings the oil in the lamp may turn white. Warm the jar in a bowl of warm water before filling, as explained in [why coconut oil turns white](/why-does-coconut-oil-turn-white).",
        ],
      },
      {
        id: "safety",
        heading: "How to keep a lit diya safe at home",
        list: [
          "Never leave a lit lamp alone in a room, especially at night.",
          "Place it on a steady brass plate or tray, on a surface that cannot burn.",
          "Keep it well away from curtains, flowers, paper, clothes and dupattas.",
          "Keep it out of reach of children and pets.",
          "Do not pour oil into a lamp while it is burning. Put it out first and let it cool.",
          "Put out the flame by pulling the wick down into the oil with a clean stick, or with a snuffer, rather than blowing hard.",
          "Keep a lid or damp cloth nearby. Never throw water on burning oil.",
        ],
      },
      {
        id: "objection",
        heading: "Is it a waste to burn edible oil in a lamp?",
        paragraphs: [
          "It is a fair thought, especially for a cold pressed oil that costs more than lamp oil. A diya uses only a little oil each day, and many families feel strongly that the lamp deserves pure oil. The fact that you can cook with the same oil you light the lamp with tells you what is, and is not, inside it.",
          "Karijeeva is one ingredient, pressed from copra from our family farms, with no mineral oil or cheaper oils blended in, and each batch is tested at an independent food testing lab ([read the report](/lab-reports)). A [500 ml or 1 L jar](/products) covers the pooja room and the kitchen. If you would rather try it first, the [Chintu Edition](/products/karijeeva-chintu-edition) is a 100 ml jar of the same oil.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can we use coconut oil for diya?",
        a: "Yes. Coconut oil has long been used for lamps in South Indian homes and temples. Use a clean cotton wick, keep only a short tip above the oil and never leave the lamp unattended.",
      },
      {
        q: "Which oil is best for pooja lamp, coconut oil or ghee?",
        a: "It depends on your family custom. Ghee, sesame oil, coconut oil and mustard oil are all used in different regions. Choose what your tradition follows, and use a clean, pure oil.",
      },
      {
        q: "Can I use cooking coconut oil for the pooja lamp?",
        a: "Yes, and many families prefer to. A food grade, single ingredient oil is pure enough to cook with, which is a good sign for the lamp too.",
      },
      {
        q: "Why does my coconut oil diya keep going out in winter?",
        a: "Coconut oil sets solid below about 24 degrees Celsius, so the wick cannot draw it up. Melt the oil in warm water before filling, and pull out a little more wick.",
      },
      {
        q: "Why does my diya make black smoke?",
        a: "Usually the wick is too long above the oil. Pull it back so only a short tip shows, and trim off any charred part before relighting.",
      },
    ],
    sources: [SRC.fssaiStd, SRC.jaocs],
    related: [
      { label: "Why coconut oil turns white", to: "/why-does-coconut-oil-turn-white" },
      { label: "Can hair coconut oil be used for cooking", to: "/can-i-use-hair-coconut-oil-for-cooking" },
      { label: "How to check coconut oil purity", to: "/how-to-check-coconut-oil-purity" },
      { label: "Shop Karijeeva", to: "/products" },
    ],
  },

  /* ---------------------------------------------------------------- */
  /* 8. Hair oil for cooking                                           */
  /* ---------------------------------------------------------------- */
  {
    slug: "can-i-use-hair-coconut-oil-for-cooking",
    template: "use",
    keyword: "can we use hair coconut oil for cooking",
    title: "Can you use hair coconut oil for cooking? Read the label first",
    seoTitle: "Can We Use Hair Coconut Oil for Cooking? Read This",
    seoDescription:
      "Can we use hair coconut oil for cooking? How to tell edible FSSAI coconut oil from hair and cosmetic oils, what to check on the label, and when to say no.",
    tldr:
      "Only if the label says it is edible. An edible coconut oil carries an FSSAI licence number and is sold as food. Many oils sold for hair are labelled as cosmetics and may contain perfume, colour, other oils or additives, and some say for external use only. Those should never go in food. A single ingredient, food grade coconut oil like Karijeeva can be used for both cooking and hair.",
    updated: UPDATED,
    bestFor: "1 L",
    bestForWhy:
      "A 1 L jar suits a home that cooks with coconut oil and also uses it for champi, so one pure oil does both jobs.",
    sections: [
      {
        id: "short-answer",
        heading: "Why is this question so common?",
        paragraphs: [
          "In many Indian homes the same blue or green bottle was used for the Sunday champi and, when the kitchen tin ran out, for a quick tadka too. Coconut oil is coconut oil, the thinking goes. Sometimes that is true, and sometimes it is not.",
          "The difference is in the label. Food and cosmetics are governed by different laws in India. An edible oil must meet FSSAI food standards, while a product sold for hair is treated as a cosmetic, and cosmetics can contain things you would not want to eat.",
        ],
      },
      {
        id: "labels",
        heading: "Edible coconut oil vs hair oil: how to read the label",
        table: {
          caption: "What to check on a coconut oil label",
          head: ["Check", "Edible coconut oil", "Oil labelled for hair or cosmetic use"],
          rows: [
            ["Licence", "FSSAI logo and licence number", "Often no FSSAI number, may show a cosmetic licence"],
            ["Ingredients", "Coconut oil only", "May list perfume, colour, other oils, vitamins or preservatives"],
            ["Wording", "Edible oil, for cooking, nutrition information", "For hair, for external use only, not for consumption"],
            ["Standard it must meet", "FSSAI coconut oil standard for food", "Cosmetic rules, not food standards"],
            ["Can you cook with it", "Yes", "No, unless it is also labelled as edible with an FSSAI number"],
          ],
        },
        paragraphs: [
          "FSSAI's standard for coconut oil says it must be clear and free from rancidity, suspended matter, added colour or flavour, and mineral oil. A hair oil does not have to meet that food standard, and many do not claim to.",
        ],
      },
      {
        id: "steps",
        heading: "How to check any jar in your house",
        steps: [
          "Look for the FSSAI logo and licence number on the label.",
          "Read the ingredient list. For edible coconut oil it should say coconut oil and nothing else.",
          "Look for words like for external use only, for hair or not for consumption. If you see them, keep that jar out of the kitchen.",
          "Smell it. Perfume or a strong floral note means something was added.",
          "If you are still unsure, do the FSSAI fridge test. It can catch gross mixing with liquid oils, but not every adulterant.",
        ],
        paragraphs: [
          "Our [purity guide](/how-to-check-coconut-oil-purity) explains the fridge test and its limits in detail.",
        ],
      },
      {
        id: "why-it-matters",
        heading: "Why does purity matter even for edible coconut oil?",
        paragraphs: [
          "Adulteration of coconut oil is a real problem in India. In 2018 Kerala's Commissioner of Food Safety banned 74 coconut oil brands in December alone, after 45 in May and 51 in June, mostly for mixing in cheaper oils. So an FSSAI number is the starting point, not the finish line.",
          "That is why we publish our lab results. Every Karijeeva batch goes to an independent food testing lab, and the latest report is on our [lab reports page](/lab-reports). The batch number is printed on every label, and support will send that batch's report if you ask.",
        ],
      },
      {
        id: "objection",
        heading: "Why not buy one cheap hair oil and one cooking oil?",
        paragraphs: [
          "You can, and plenty of people do. The case for one jar is simple: if an oil is pure enough to eat, you know exactly what is going on your scalp too. Karijeeva is food grade coconut oil with an FSSAI licence, one ingredient, not refined, bleached or deodorised.",
          "Keep it clean for the kitchen by pouring hair oil into a separate katori, never dipping oily fingers into the main jar. Or keep a small [Chintu Edition](/products/karijeeva-chintu-edition) on the dressing table for champi and a [1 L jar](/products) in the kitchen, both the same oil. For cooking ideas, see [cooking with coconut oil](/cooking-with-coconut-oil).",
        ],
      },
    ],
    faqs: [
      {
        q: "Can we use hair coconut oil for cooking?",
        a: "Only if the label shows an FSSAI licence number and says it is edible, with coconut oil as the only ingredient. If it says for hair or for external use only, do not cook with it.",
      },
      {
        q: "Can we use edible coconut oil for hair?",
        a: "Yes. Many families use the same single ingredient, edible coconut oil for champi. Pour it into a katori so the main jar stays clean for cooking.",
      },
      {
        q: "Is every coconut hair oil edible?",
        a: "No. Read the label of the exact jar you have: look for an FSSAI licence number, the word edible and a one line ingredient list. Some packs are sold as edible and some as hair oil, so check every time.",
      },
      {
        q: "How do I know if coconut oil is food grade?",
        a: "Look for the FSSAI logo and licence number, coconut oil as the only ingredient, and nutrition information on the label. A brand that publishes independent lab reports gives you extra confidence.",
      },
      {
        q: "Is Karijeeva coconut oil edible?",
        a: "Yes. Karijeeva is cold pressed coconut oil sold as food under FSSAI licence 11226304000242, with coconut as the only ingredient. Many customers use the same oil for hair and skin.",
      },
    ],
    sources: [SRC.fssaiStd, SRC.fssaiLabel, SRC.kerala, SRC.dart],
    related: [
      { label: "How to check coconut oil purity", to: "/how-to-check-coconut-oil-purity" },
      { label: "Cold pressed vs refined coconut oil", to: "/cold-pressed-vs-refined-coconut-oil" },
      { label: "How to oil your hair with coconut oil", to: "/how-to-oil-hair-with-coconut-oil" },
      { label: "Our lab reports", to: "/lab-reports" },
    ],
  },

  /* ---------------------------------------------------------------- */
  /* 9. Turns white                                                    */
  /* ---------------------------------------------------------------- */
  {
    slug: "why-does-coconut-oil-turn-white",
    template: "use",
    keyword: "why coconut oil turns white in winter",
    title: "Why does coconut oil turn white in winter?",
    seoTitle: "Why Coconut Oil Turns White in Winter (It Is Normal)",
    seoDescription:
      "Why coconut oil turns white in winter: it sets solid below about 24 degrees Celsius, which is normal. How to melt it and what the FSSAI fridge test shows.",
    tldr:
      "Coconut oil turns white and solid when the room drops below about 24 degrees Celsius. That is normal and does not mean it has gone bad. It happens because coconut oil is mostly saturated fat. Stand the jar in warm water to melt it, and judge freshness by smell, not colour. FSSAI's fridge test uses this same property to spot gross mixing with liquid oils.",
    updated: UPDATED,
    bestFor: "500 ml",
    bestForWhy:
      "A 500 ml jar is easy to stand in a bowl of warm water on a cold morning.",
    sections: [
      {
        id: "why",
        heading: "Why does coconut oil go white and solid?",
        paragraphs: [
          "Most oils are liquid at room temperature because their fats have many double bonds. Coconut oil has very few, which lab tests measure as a low iodine value, so it behaves more like ghee than like groundnut oil. Below about 24 degrees Celsius it starts to set, first cloudy, then white and firm.",
          "In Bengaluru, Delhi or any hill station in winter, and in any air conditioned room, that means your jar will often be white. On the Karnataka coast in summer it stays clear. Same oil, different weather.",
        ],
      },
      {
        id: "normal",
        heading: "Is white coconut oil spoiled?",
        paragraphs: [
          "No. Turning white is a change of state, like water turning to ice. It does not change the oil, and it melts back to clear liquid when warmed. You can let it melt and set many times.",
          "What does tell you oil has gone stale is smell and taste. Fresh coconut oil smells of coconut. A sharp, sour or paint like smell means it has gone rancid, whatever its colour.",
        ],
        table: {
          caption: "What you see in the jar, and what it means",
          head: ["What you see", "What it means", "What to do"],
          rows: [
            ["Clear liquid", "Room is above about 24 degrees Celsius", "Nothing"],
            ["Cloudy, with white bits floating", "Oil is starting to set", "Nothing, or warm it to clear it"],
            ["Fully white and solid", "Room is cool, normal for coconut oil", "Warm in a water bath before pouring"],
            ["Liquid layer on top of white oil in the fridge test", "Possible mixing with other liquid oils", "Read our purity guide, linked below"],
            ["Sour or paint like smell", "The oil has gone rancid", "Do not use it for food"],
          ],
        },
      },
      {
        id: "melt",
        heading: "How to melt solid coconut oil safely",
        steps: [
          "Close the cap tightly.",
          "Stand the jar in a bowl of warm water, not boiling, for five to ten minutes.",
          "Turn the jar now and then so the oil melts evenly.",
          "If you need only a little, scoop some out with a clean, dry spoon and warm the spoon or a katori over hot water.",
          "Never put the jar on a flame, in a hot oven or in the microwave.",
          "Keep water out of the jar. Dry the outside before opening it.",
        ],
        paragraphs: [
          "For cooking, you do not need to melt it first. Scoop the solid oil straight into a hot kadai and it melts in seconds. For [hair](/how-to-oil-hair-with-coconut-oil) or a [body massage](/coconut-oil-for-body-massage), melt it in a water bath.",
        ],
      },
      {
        id: "fridge-test",
        heading: "What is the FSSAI fridge test for coconut oil?",
        paragraphs: [
          "FSSAI's DART guide uses this setting property as a quick home check. Put some coconut oil in a glass in the refrigerator (not the freezer) for 30 minutes. Pure coconut oil sets solid. If other liquid oils are mixed in, they stay as a separate liquid layer.",
          "It is a useful first check, but it has limits. It only catches gross mixing with liquid oils, and it will not catch palm kernel oil, which also sets solid. Our [coconut oil purity guide](/how-to-check-coconut-oil-purity) explains more, and our [lab reports](/lab-reports) show the full tests a lab runs.",
        ],
      },
      {
        id: "objection",
        heading: "My old coconut oil never turned white. Is something wrong with this one?",
        paragraphs: [
          "Not with this one. Pure coconut oil sets when it is cool, so an oil that stays fully liquid in a cold room is worth a second look. It may simply have been kept somewhere warm, or it may have other oils mixed in, which is exactly what the fridge test checks.",
          "Try the fridge test on any jar, including ours. Karijeeva is one ingredient, cold pressed coconut oil, and each batch is tested at an independent food testing lab. See our [jars here](/products), or start small with the 100 ml [Chintu Edition](/products/karijeeva-chintu-edition).",
        ],
      },
    ],
    faqs: [
      {
        q: "Why does coconut oil freeze in winter?",
        a: "Coconut oil is mostly saturated fat, so it sets solid below about 24 degrees Celsius. This is normal and does not mean the oil is spoiled. Warm the jar in water to melt it.",
      },
      {
        q: "Is white coconut oil pure?",
        a: "Turning white in the cold is what pure coconut oil does. The FSSAI fridge test uses this: pure coconut oil sets fully, while mixed in liquid oils stay as a separate layer. It does not catch every adulterant, so a lab report gives more certainty.",
      },
      {
        q: "Can I use coconut oil after it turns white?",
        a: "Yes. It melts back to clear liquid when warmed, and it can melt and set many times. Check the smell, not the colour, to know if it is fresh.",
      },
      {
        q: "How to melt coconut oil without a microwave?",
        a: "Stand the closed jar in a bowl of warm water for five to ten minutes, turning it now and then. For small amounts, warm a spoonful in a katori over hot water.",
      },
      {
        q: "Why is my coconut oil half white and half clear?",
        a: "It is partly set, which happens when the room is close to about 24 degrees Celsius. If you see a clear layer in the fridge test after 30 minutes, though, other oils may be mixed in.",
      },
    ],
    sources: [SRC.jaocs, SRC.dart, SRC.dartSummary, SRC.fssaiStd],
    related: [
      { label: "How to check coconut oil purity", to: "/how-to-check-coconut-oil-purity" },
      { label: "Our lab reports", to: "/lab-reports" },
      { label: "Coconut oil for diya", to: "/coconut-oil-for-diya" },
      { label: "Can hair coconut oil be used for cooking", to: "/can-i-use-hair-coconut-oil-for-cooking" },
    ],
  },
];
